/**
 * Post Composer + AI Copywriter — Learn-Mode inline tips.
 *
 * Each `key` is wired to a `data-help-context="<key>"` attribute on the
 * matching section of `routes/social/PostComposer.tsx`,
 * `routes/social/CopywriterChat.tsx`, and the `components/composer/*` /
 * `components/PlatformPreview.tsx` / `components/CopywriterContextChip.tsx`
 * pieces they render. Tone matches the portal's client-facing tips: plain
 * language, 1-3 sentence body, jargon defined in bullets.
 */

import type { LearnTip } from "./types";

export const composerTips: LearnTip[] = [
  {
    key: "social-composer-title",
    title: "Post Title",
    body: "An optional label that helps you and your client recognize this post in approvals, the calendar, and lists. The title is never published to any platform.",
    bullets: [
      "Leave it blank and the post is identified by the start of its caption.",
      "Keep it short, for example the campaign or event name.",
    ],
  },
  {
    key: "social-composer-caption",
    title: "Writing Your Post",
    body: "Type the post text here. If you've selected more than one platform, use the tabs to write a single caption for all of them, or switch to a per-network tab to customize the wording for just that platform.",
    bullets: [
      "All — one caption shared across every selected platform.",
      "Per-platform tabs appear once you select more than one network, so you can tailor length or tone for each.",
    ],
  },
  {
    key: "social-composer-ig-grid",
    title: "Instagram Grid Preview",
    body: "Shows how this post will sit among your Instagram account's most recent posts, so you can check your visual grid stays balanced before you publish.",
    bullets: [
      "The highlighted tile is this draft's spot in the grid once it's scheduled.",
      "Select an Instagram account under “Publish To” to see a preview here.",
    ],
  },
  {
    key: "social-composer-schedule",
    title: "Scheduling Your Post",
    body: "Pick a future date and time to queue this post, or leave it blank to publish right away. Times are shown in the client's local timezone, not yours.",
  },
  {
    key: "social-composer-add-to-queue",
    title: "Add to Queue",
    body: "Saves the post and drops it into the next free posting slot for the first network's account — no need to pick a time yourself.",
    bullets: [
      "Slots come from the Posting schedule page; an account with no slots asks you to set some up.",
      "If every slot is full for the next 8 weeks the post waits and takes the first slot that opens.",
      "The Approve now box works the same as for Schedule — unchecked, the post waits in Approvals.",
    ],
  },
  {
    key: "social-composer-extras",
    title: "Post Extras",
    body: "Optional add-ons that go out with the post: a first comment (useful for extra hashtags on Instagram and Facebook), a link to attach, and UTM tracking for that link.",
    bullets: [
      "First comment — posts automatically right after the main post goes live, so hashtags don't clutter the caption.",
      "Link — Facebook shows it as a link card, LinkedIn appends it, Google Business adds a “Learn more” button.",
    ],
  },
  {
    key: "social-composer-utm",
    title: "UTM Link Tracking",
    body: "Tags the link in this post with the network, “social”, and a campaign name when it publishes, so Google Analytics can show exactly which posts are driving visits. Links that already have tracking tags are left untouched.",
    bullets: [
      "An organization can turn this on by default for every new post in Settings — this checkbox only overrides it for the current post.",
    ],
  },
  {
    key: "social-composer-media",
    title: "Post Media",
    body: "Attach the images or video for this post. Each connected network has its own size, shape, and length limits — we check your media against them before you can publish.",
    bullets: [
      "Images upload as JPG for Instagram automatically.",
      "Video publishes only to the networks that support video for this post.",
    ],
  },
  {
    key: "social-composer-networks",
    title: "Publish To",
    body: "Choose which connected accounts this post goes out to. Only platforms with an active, connected account can be selected — greyed-out rows need to be reconnected first on the Accounts page.",
  },
  {
    key: "social-composer-video",
    title: "Video Options",
    body: "Appears only when a video is attached and Facebook or Instagram is selected. Choose how the video is presented on each platform — a Facebook Page video or Reel, and whether an Instagram Reel also shares to the main feed.",
  },
  {
    key: "social-composer-sponsorship",
    title: "Paid Partnerships",
    body: "Tick this when the post is sponsored or paid for by a brand. Instagram, TikTok and YouTube then show their own “Paid partnership” label automatically.",
    bullets: [
      "The caption still needs a clear disclosure such as #ad — sponsored posts without one are held before publishing.",
      "Brand partner IDs (Instagram) tag the sponsoring brand on the label.",
    ],
  },
  {
    key: "social-composer-facebook-options",
    title: "Facebook Options",
    body: "Publish to the Page's Story instead of the feed. A Story shows for 24 hours at the top of the Page.",
    bullets: [
      "Stories use exactly one image or one vertical (9:16) video of up to 60 seconds.",
      "The caption and link aren't shown on a Story — put any text you need into the image or video itself.",
      "A Story is neither a Page video nor a Reel, so the Reel choice under Video options is hidden while it's on.",
    ],
  },
  {
    key: "social-composer-instagram-options",
    title: "Instagram Options",
    body: "Publish as a Story, post a single video as a Trial Reel, or invite up to three collaborators to share the post on their profiles.",
    bullets: [
      "Stories use one image or video and no caption. Link stickers have to be added in the Instagram app.",
      "Trial Reels are shown to non-followers first; share them with followers yourself or automatically when they perform well.",
      "Collaborators can't be added to Stories or Trial Reels.",
    ],
  },
  {
    key: "social-composer-instagram-audio",
    title: "Instagram Audio",
    body: "Attach a track from Instagram's own library — its royalty-free Sound Collection, or a creator's public original sound — to a single-video Reel. Instagram mixes it in when the Reel publishes; it's never added to your video file, so your download stays clean.",
    bullets: [
      "Audio picks come from Instagram's library, not a trend chart — the filter box searches the tracks collected here, since Instagram doesn't support keyword search for audio yet.",
      "Original sounds have no preview — use “Listen on Instagram” to check them before using.",
      "If the attached video already has music baked in, you'll be warned before mixing in Instagram audio too.",
    ],
  },
  {
    key: "social-composer-threads-options",
    title: "Threads Options",
    body: "Add a topic so the post shows up in that Threads conversation, choose who can reply, or turn a text-only post into a poll with 2–4 options.",
    bullets: ["A link on a text-only Threads post shows as a preview card.", "Polls can't include media or a link."],
  },
  {
    key: "social-composer-tiktok-options",
    title: "TikTok Post Settings",
    body: "TikTok requires you to pick who can see each direct post and whether people can comment, Duet or Stitch it. Nothing is preselected — choose before publishing.",
    bullets: [
      "Options greyed out here are turned off in the creator's own TikTok settings.",
      "Disclose commercial content when the video promotes your business (“Your brand”) or another brand (“Branded content”). Branded content can't be private.",
    ],
  },
  {
    key: "social-composer-gbp-options",
    title: "Google Business Profile Options",
    body: "Post a regular update, an event with dates, or an offer with an optional coupon code, and choose the button people see (Book, Order online, Call now and more).",
    bullets: ["Buttons other than Call now open the post's link, so add one in Post extras."],
  },
  {
    key: "social-composer-youtube-options",
    title: "YouTube Options",
    body: "Set an optional title (otherwise the caption is used), choose who can see the video, and answer Made for Kids — YouTube requires that answer before it will accept the upload.",
    bullets: [
      "Made for Kids has no default — pick Yes or No before publishing.",
      "Private and Unlisted videos still count toward the channel's upload, just aren't publicly listed/searchable.",
    ],
  },
  {
    key: "social-composer-checks",
    title: "Pre-Publish Check",
    body: "Runs the same validation the server does before accepting your post, so you can catch problems here instead of at publish time.",
    bullets: [
      "Red items are errors that will block publishing — fix them first.",
      "Amber items are warnings — the post can still go out, but double-check them.",
    ],
  },
  {
    key: "social-composer-preview",
    title: "Platform Preview",
    body: "Shows roughly how this post will look on the selected platform, along with a live character count against that platform's limit. The counter turns red once you go over.",
  },
  {
    key: "social-composer-insert",
    title: "Insert Snippets, Hashtag Groups and Templates",
    body: "Drop saved text into the caption or the first comment without retyping it.",
    bullets: [
      "Snippets — reusable text like offers or sign-offs. {{brand_name}}, {{booking_url}}, {{phone}} and {{website}} are filled in from this client automatically.",
      "Hashtag groups — your saved sets. You'll get a warning before going over Instagram's 30-hashtag limit, with the option to put them in the first comment instead.",
      "Caption templates — start from a saved template (replaces the current caption after you confirm).",
    ],
  },
  {
    key: "social-composer-media-picker",
    title: "Add Media from Your Library",
    body: "Attach designs, the client's shared asset library or free stock photos without leaving the post. Everything you pick is added after the media already on the post.",
    bullets: [
      "A multi-page design is attached as a carousel, one image per page.",
      "Client assets come from the shared library for the client selected at the top.",
    ],
  },
  {
    key: "social-composer-save-template",
    title: "Save as Template",
    body: "Saves this post's caption, hashtags, networks, media, first comment and link as a reusable template — for this client only, or for every client.",
  },
  {
    key: "social-composer-edit-mode",
    title: "Editing a Saved Post",
    body: "You're changing an existing post — saving updates it in place instead of creating a copy. Reschedule or Unschedule it from the buttons at the bottom.",
    bullets: [
      "If this client requires approval, changing an approved post's content sends it back to Approvals, and it won't publish until it's approved again.",
      "Already-published networks aren't changed — edits only affect networks that haven't posted yet.",
    ],
  },
  {
    key: "social-composer-link-in-bio",
    title: "Add to Link in Bio",
    body: "Attaches a link to this post on the client's link-in-bio page. When followers tap this post's tile in the page's Instagram grid, they go straight to the link — with your page's UTM tracking added.",
    bullets: [
      "Shows when the post goes to Instagram (feed or Reel) or TikTok and the client has a published link-in-bio page.",
      "The link appears on the tile once the post is live on Instagram. Stories never show in the grid.",
      "Turn it off and save to remove the link from the page. You can also edit it later under Link in Bio → Instagram grid.",
    ],
  },
  {
    key: "social-post-edit",
    title: "Edit This Post",
    body: "Opens the post in the composer so you can change the caption, media, networks or time. Saving updates this post — nothing new is created. Each published network also shows its latest numbers and a link to the live post.",
  },
  {
    key: "social-copywriter-context",
    title: "Brand Voice Check",
    body: "Flags whether the AI's draft matches this client's brand voice rules. A mic icon means it's on-brand; a warning icon means the wording drifted from the voice guide and may need a look before you use it.",
  },
  {
    key: "social-copywriter-chat",
    title: "AI Copywriter",
    body: "Chat with an AI assistant that knows this client's brand voice and recent posts to draft captions, hooks, or replies. Start a new chat from the sidebar, or reopen a past conversation to keep refining a draft.",
  },
  {
    key: "social-composer-proposed-time",
    title: "Proposed Publish Time",
    body: "The date and time you picked is saved as a proposal, not a booking. Reviewers see it and can approve and schedule in one click; nothing publishes until someone approves it.",
  },
  {
    key: "composer-campaign-tags",
    title: "Campaign Tags",
    body: "Label this post with one or more campaign tags (for example a promotion or a season). Press Enter or a comma to add a tag.",
    bullets: [
      "Tags never appear in the published post — they're for your reporting only.",
      "Analytics → Campaigns totals reach and engagement per tag, so tag consistently to compare campaigns.",
    ],
  },
];
